import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import AdminBillingNav from '@/components/adminBilling/AdminBillingNav';
import StatusChip from '@/components/adminBilling/StatusChip';
import { useToast } from '@/components/Toast';
import { EmptyState } from '@/components/ui';
import { adminBillingService, formatCents, type AdminBillingOrganization } from '@/api/services/adminBilling';
import { billingAdminService, type BillingPlan, type OrgUsageSummary } from '@/api/services/billingAdmin';
import { formatApiError } from '@/utils/formatApiError';
import {
  applyGrantToOrganizationBilling,
  resolveActiveEntitlement,
  resolveEffectiveOrganizationPlanId,
  resolveEffectiveOrganizationPlanSlug,
} from '@/utils/effectiveOrganizationPlan';
import { invalidatePlanDependentCaches, usePlanChangeListener } from '@/utils/planRefresh';
import {
  expiryInputToIso,
  findPlanIdForSlug,
  formatGrantPlanOptionLabel,
} from '@/utils/billingPlanForm';
import ConfirmActionModal from '@/components/ConfirmActionModal';
import ResetOrganizationUsageModal from '@/components/admin/ResetOrganizationUsageModal';
import { useMe } from '@/context/MeContext';
import { useWorkspace } from '@/context/WorkspaceContext';

function planDisplayName(
  data: AdminBillingOrganization,
  plans: BillingPlan[],
): string {
  const slug = resolveEffectiveOrganizationPlanSlug(data);
  const fromList = plans.find((plan) => plan.slug === slug)?.name;
  const fromEntitlement = resolveActiveEntitlement(data)?.planName;
  return String(fromList || fromEntitlement || slug || '—');
}

export default function AdminBillingOrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { me } = useMe();
  const { invalidateWorkspaces } = useWorkspace();
  const { toast } = useToast();
  const canManage = Boolean(me?.permissions.canManageAdminBilling);
  const grantFormRef = useRef<HTMLDivElement | null>(null);

  const [data, setData] = useState<AdminBillingOrganization | null>(null);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [grantExpiry, setGrantExpiry] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmGrant, setConfirmGrant] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);
  const [usage, setUsage] = useState<OrgUsageSummary | null>(null);
  const [resetUsageOpen, setResetUsageOpen] = useState(false);

  const activePlans = useMemo(() => plans.filter((plan) => plan.isActive), [plans]);
  const effectivePlanId = useMemo(
    () => resolveEffectiveOrganizationPlanId(plans, data),
    [plans, data],
  );
  const selectedPlanId = pendingPlanId ?? effectivePlanId;

  const loadAll = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const [detail, planList, usageSummary] = await Promise.all([
        adminBillingService.organization(id),
        canManage ? billingAdminService.listPlans(true) : Promise.resolve({ items: [] as BillingPlan[] }),
        billingAdminService.usage(id).catch(() => null),
      ]);
      setData(detail);
      setPlans(planList.items);
      setUsage(usageSummary);
      setPendingPlanId(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [canManage, id]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  usePlanChangeListener(loadAll);

  const focusGrantForm = () => {
    grantFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const executeGrant = async () => {
    if (!id || !selectedPlanId) return;
    setBusy(true);
    setError(null);
    try {
      const granted = await adminBillingService.grant(id, {
        planId: selectedPlanId,
        expiresAt: expiryInputToIso(grantExpiry),
        reason: 'admin_console_grant',
      });
      setPendingPlanId(null);
      setData((prev) => (prev ? applyGrantToOrganizationBilling(prev, granted, plans) : prev));
      toast('Plan granted', 'success');
      await loadAll();
      await invalidatePlanDependentCaches({ invalidateWorkspaces });
    } catch (e) {
      const message = formatApiError(e);
      setError(message);
      toast(message, 'error');
    } finally {
      setBusy(false);
      setConfirmGrant(false);
    }
  };

  const executeRevoke = async (entitlementId: string) => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await adminBillingService.revoke(id, entitlementId, 'admin_console_revoke');
      toast('Entitlement revoked', 'success');
      setPendingPlanId(null);
      await loadAll();
      await invalidatePlanDependentCaches({ invalidateWorkspaces });
    } catch (e) {
      const message = formatApiError(e);
      setError(message);
      toast(message, 'error');
    } finally {
      setBusy(false);
      setRevokeTarget(null);
    }
  };

  const prefillChangePlan = (planSlug?: string | null) => {
    setPendingPlanId(findPlanIdForSlug(plans, planSlug));
    focusGrantForm();
  };

  const prefillExtend = () => {
    focusGrantForm();
    grantFormRef.current?.querySelector<HTMLInputElement>('input[type="datetime-local"]')?.focus();
  };

  const executeResetUsage = async (payload: {
    resetSearchUsage: boolean;
    resetNotificationUsage: boolean;
    resetJobUsage: boolean;
    reason: string;
  }) => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const result = await adminBillingService.resetUsage(id, payload);
      toast('Organization usage reset', 'success');
      setResetUsageOpen(false);
      await loadAll();
      setUsage(result.usage as OrgUsageSummary);
      await invalidatePlanDependentCaches({ invalidateWorkspaces });
    } catch (e) {
      const message = formatApiError(e);
      setError(message);
      toast(message, 'error');
      throw e;
    } finally {
      setBusy(false);
    }
  };

  if (!id) return null;

  const current = resolveActiveEntitlement(data);

  return (
    <div className="admin-billing-page admin-billing-org-detail">
      <AdminBillingNav />
      <PageHeader
        title={String(data?.organization?.name || 'Organization billing')}
        subtitle="Plan, entitlements, payments, and audit history."
      >
        <Link to="/admin/billing/organizations">← All organizations</Link>
      </PageHeader>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {!data ? (
        <div className="panel">Loading…</div>
      ) : (
        <>
          <section className="card admin-billing-subscription-card">
            <div className="admin-billing-subscription-card__header">
              <div>
                <h2 className="admin-billing-card-title">Current subscription</h2>
                <dl className="admin-billing-meta-grid">
                  <div>
                    <dt>Plan</dt>
                    <dd>{planDisplayName(data, plans)}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>
                      <StatusChip status={String(data.health?.lifecycleState || data.subscription?.status || '—')} />
                    </dd>
                  </div>
                  <div>
                    <dt>Expires</dt>
                    <dd>{data.health?.expiresAt ? new Date(String(data.health.expiresAt)).toLocaleString() : '—'}</dd>
                  </div>
                  <div>
                    <dt>Grace until</dt>
                    <dd>{data.health?.graceUntil ? new Date(String(data.health.graceUntil)).toLocaleString() : '—'}</dd>
                  </div>
                </dl>
              </div>
              {canManage ? (
                <div className="table-action-bar admin-billing-subscription-actions">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => prefillChangePlan(resolveEffectiveOrganizationPlanSlug(data))}
                  >
                    Change plan
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={prefillExtend}>
                    Extend expiry
                  </button>
                  {current ? (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      disabled={busy}
                      onClick={() => setRevokeTarget(String(current.id))}
                    >
                      Revoke
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </section>

          <section className="card admin-billing-usage-card">
            <div className="admin-billing-subscription-card__header">
              <div>
                <h2 className="admin-billing-card-title">Usage</h2>
                {usage ? (
                  <dl className="admin-billing-meta-grid">
                    <div>
                      <dt>Searches</dt>
                      <dd>
                        {usage.usage.activeSearches}
                        {usage.limits.maxSearches != null ? ` / ${usage.limits.maxSearches}` : ' / Unlimited'}
                      </dd>
                    </div>
                    <div>
                      <dt>Notifications today</dt>
                      <dd>
                        {usage.usage.notificationsSentToday}
                        {usage.limits.maxNotificationsPerDay != null
                          ? ` / ${usage.limits.maxNotificationsPerDay}`
                          : ' / Unlimited'}
                      </dd>
                    </div>
                    <div>
                      <dt>Recipients</dt>
                      <dd>
                        {usage.usage.recipients}
                        {usage.limits.maxNotificationRecipients != null
                          ? ` / ${usage.limits.maxNotificationRecipients}`
                          : ' / Unlimited'}
                      </dd>
                    </div>
                  </dl>
                ) : (
                  <p className="muted">Usage summary is not available.</p>
                )}
              </div>
              {canManage ? (
                <div className="table-action-bar admin-billing-subscription-actions">
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    disabled={busy}
                    onClick={() => setResetUsageOpen(true)}
                  >
                    Reset usage
                  </button>
                </div>
              ) : null}
            </div>
          </section>

          {canManage ? (
            <section className="card admin-billing-grant-card billing-form-card" ref={grantFormRef}>
              <h2 className="admin-billing-card-title billing-form-card__title">Change plan</h2>
              <div className="billing-form-grid">
                <label className="billing-form-grid__field billingGrantForm__plan">
                  Plan
                  <select
                    value={selectedPlanId}
                    onChange={(e) => setPendingPlanId(e.target.value)}
                    disabled={!activePlans.length || busy}
                  >
                    {!activePlans.length ? <option value="">No active plans</option> : null}
                    {activePlans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {formatGrantPlanOptionLabel(plan)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="billing-form-grid__field billingGrantForm__expiry">
                  Expiry date
                  <input
                    type="datetime-local"
                    value={grantExpiry}
                    onChange={(e) => setGrantExpiry(e.target.value)}
                    disabled={busy}
                  />
                  <span className="muted admin-billing-field-hint">Optional. Leave blank for no expiry (never expires).</span>
                </label>
                <div className="billing-form-grid__actions">
                  <button
                    type="button"
                    className="btn btn-primary admin-billing-grant-button billingGrantForm__action"
                    disabled={busy || !selectedPlanId}
                    onClick={() => setConfirmGrant(true)}
                  >
                    Grant / change plan
                  </button>
                </div>
              </div>
            </section>
          ) : null}

          <section className="card admin-billing-entitlements-card">
            <h2 className="admin-billing-card-title">Entitlements</h2>
            <table className="data-table data-table--compact">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Expires</th>
                  {canManage ? <th>Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {data.entitlements.length === 0 ? (
                  <tr>
                    <td colSpan={canManage ? 5 : 4}>
                      <EmptyState title="No entitlements" description="Grant a plan to create the first entitlement." />
                    </td>
                  </tr>
                ) : (
                  data.entitlements.map((item) => (
                    <tr key={String(item.id)}>
                      <td>{String(item.planName || item.planSlug || '—')}</td>
                      <td>
                        <StatusChip status={String(item.status)} />
                      </td>
                      <td>{String(item.source)}</td>
                      <td>{item.expiresAt ? new Date(String(item.expiresAt)).toLocaleDateString() : '—'}</td>
                      {canManage ? (
                        <td>
                          <div className="table-action-bar">
                            {['active', 'manual_grant'].includes(String(item.status)) ? (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  disabled={busy}
                                  onClick={() => prefillChangePlan(String(item.planSlug || ''))}
                                >
                                  Change plan
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  disabled={busy}
                                  onClick={prefillExtend}
                                >
                                  Extend
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  disabled={busy}
                                  onClick={() => setRevokeTarget(String(item.id))}
                                >
                                  Revoke
                                </button>
                              </>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>

          <section className="card admin-billing-payments-card">
            <h2 className="admin-billing-card-title">Payments</h2>
            {!data.payments.length ? (
              <EmptyState title="No payments recorded yet." description="Stripe and manual payments will appear here." />
            ) : (
              <table className="data-table data-table--compact">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Provider</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.map((payment) => (
                    <tr key={String(payment.id)}>
                      <td>{payment.createdAt ? new Date(String(payment.createdAt)).toLocaleString() : '—'}</td>
                      <td>{formatCents(Number(payment.amountCents))}</td>
                      <td>
                        <StatusChip status={String(payment.status)} />
                      </td>
                      <td>{String(payment.provider)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="card">
            <h2 className="admin-billing-card-title">Provider customers</h2>
            {!data.providerCustomers.length ? (
              <EmptyState title="No provider customers" description="Stripe or other billing providers are not linked yet." />
            ) : (
              <ul className="admin-billing-provider-list">
                {data.providerCustomers.map((customer, index) => (
                  <li key={index}>
                    {String(customer.provider)}: {String(customer.providerCustomerId)}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card">
            <h2 className="admin-billing-card-title">Audit history</h2>
            {!data.auditHistory.length ? (
              <EmptyState title="No billing audit events" description="Grants, revokes, and payment events will appear here." />
            ) : (
              <ul className="admin-billing-audit-list">
                {data.auditHistory.map((entry, index) => (
                  <li key={index}>
                    <strong>{String(entry.action)}</strong>
                    {' · '}
                    {entry.createdAt ? new Date(String(entry.createdAt)).toLocaleString() : ''}
                    {entry.reason ? ` — ${String(entry.reason)}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
      <ConfirmActionModal
        open={confirmGrant}
        title="Grant or change plan?"
        description="Apply the selected plan to this organization. Existing entitlements may be updated."
        confirmLabel="Grant / change plan"
        loading={busy}
        onCancel={() => setConfirmGrant(false)}
        onConfirm={executeGrant}
      />
      <ConfirmActionModal
        open={Boolean(revokeTarget)}
        title="Revoke entitlement?"
        description="This organization will lose access to the granted plan limits."
        confirmLabel="Revoke"
        variant="danger"
        loading={busy}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={() => (revokeTarget ? executeRevoke(revokeTarget) : undefined)}
      />
      <ResetOrganizationUsageModal
        organizationName={String(data?.organization?.name || 'this organization')}
        open={resetUsageOpen}
        loading={busy}
        onClose={() => setResetUsageOpen(false)}
        onConfirm={executeResetUsage}
      />
    </div>
  );
}
